/**
 * Les actions sensibles de l'administration, telles que le serveur les juge.
 *
 * **Miroir de `SensitiveActionKind`** (`backend/src/Administration/Application/`),
 * qui est la source : la liste fermee des motifs de chaque action, et le fait
 * qu'elle exige ou non le mot de passe de l'operateur. Ce fichier n'ajoute que
 * les libelles francais. Un code absent d'ici et present la-bas ne serait pas
 * proposable ; un code present ici et absent la-bas serait refuse par le
 * serveur (`422 validation_failed`) — les deux tables changent ensemble.
 *
 * Ajuster une liste est une decision du responsable produit, sans changement de
 * modele : les codes ne vivent que dans le journal d'audit.
 */

export type SensitiveActionKind =
  | 'revoke_sessions'
  | 'anonymize'
  | 'export'
  | 'retry_failed_message'
  | 'update_setting'
  | 'enable_maintenance'
  | 'publish_legal_document'
  | 'suspend'
  | 'reactivate'
  | 'recover_synchronization';

export interface ReasonOption {
  /** Code enregistre au journal d'audit, jamais affiche. */
  readonly code: string;
  /** Ce que l'operateur lit et choisit. */
  readonly label: string;
}

export interface SensitiveActionPolicy {
  readonly reasons: readonly ReasonOption[];
  /** L'action exige-t-elle le mot de passe de l'operateur ? */
  readonly requiresPassword: boolean;
}

/** Code de motif qui exige une precision ecrite. */
export const OTHER_REASON_CODE = 'other';

const OTHER: ReasonOption = { code: OTHER_REASON_CODE, label: 'Autre motif (à préciser)' };

export const SENSITIVE_ACTIONS: Readonly<Record<SensitiveActionKind, SensitiveActionPolicy>> = {
  recover_synchronization: {
    requiresPassword: true,
    reasons: [
      { code: 'support_request', label: 'Demande de la personne au support' },
      { code: 'transient_failure', label: 'Échec technique passager' },
      { code: 'fixed_by_deployment', label: 'Cause corrigée par un déploiement' },
      OTHER,
    ],
  },
  revoke_sessions: {
    requiresPassword: false,
    reasons: [
      { code: 'lost_device', label: 'Appareil perdu ou volé' },
      { code: 'security_compromise', label: 'Compte compromis' },
      { code: 'support_request', label: 'Demande de la personne au support' },
      OTHER,
    ],
  },
  anonymize: {
    requiresPassword: true,
    reasons: [
      { code: 'erasure_request', label: 'Demande de la personne — droit à l’effacement' },
      { code: 'legal_request', label: 'Réquisition ou obligation légale' },
      { code: 'duplicate_or_test_account', label: 'Compte en double ou de test' },
      OTHER,
    ],
  },
  export: {
    requiresPassword: true,
    reasons: [
      { code: 'access_request', label: 'Demande de la personne — droit d’accès' },
      { code: 'support_request', label: 'Demande de la personne au support' },
      { code: 'legal_request', label: 'Réquisition ou obligation légale' },
      OTHER,
    ],
  },
  retry_failed_message: {
    requiresPassword: false,
    reasons: [
      { code: 'worker_outage', label: 'Le worker était arrêté' },
      { code: 'transient_failure', label: 'Échec passager (réseau, envoi de courriel)' },
      { code: 'fixed_by_deployment', label: 'Cause corrigée par un déploiement' },
      OTHER,
    ],
  },
  update_setting: {
    requiresPassword: false,
    reasons: [
      { code: 'planned_maintenance', label: 'Maintenance planifiée' },
      { code: 'incident', label: 'Incident en cours' },
      { code: 'configuration_change', label: 'Correction de configuration' },
      OTHER,
    ],
  },
  enable_maintenance: {
    requiresPassword: true,
    reasons: [
      { code: 'planned_maintenance', label: 'Maintenance planifiée' },
      { code: 'incident', label: 'Incident en cours' },
      { code: 'security_incident', label: 'Incident de sécurité' },
      OTHER,
    ],
  },
  publish_legal_document: {
    requiresPassword: true,
    reasons: [
      { code: 'legal_review', label: 'Relecture juridique' },
      { code: 'form_correction', label: 'Correction de forme (faute, mise en page)' },
      { code: 'compliance_update', label: 'Mise en conformité' },
      OTHER,
    ],
  },
  suspend: {
    requiresPassword: true,
    reasons: [
      { code: 'abuse', label: 'Usage abusif ou contraire aux conditions' },
      { code: 'security_compromise', label: 'Compte compromis' },
      { code: 'legal_request', label: 'Réquisition ou obligation légale' },
      OTHER,
    ],
  },
  reactivate: {
    requiresPassword: false,
    reasons: [
      { code: 'resolved', label: 'Situation résolue' },
      { code: 'suspended_in_error', label: 'Suspension par erreur' },
      OTHER,
    ],
  },
};
